import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChildReview from "../components/handover/ChildReview";
import ClassroomHandoverPageShell from "../components/handover/ClassroomHandoverPageShell";
import HandoverRouteState from "../components/handover/HandoverRouteState";
import useClassroomHandoverChildren from "../hooks/useClassroomHandoverChildren";
import useHandoverIdentity from "../hooks/useHandoverIdentity";
import { destinationWithSelection, parseClassroomHandoverSelection } from "../utils/classroomHandoverSelection";

const ROUTE_STATE_COPY = {
  forbidden: ["This child review isn’t available", "You don’t have access to review this classroom."],
  not_found: ["Classroom not found", "This child review link may be out of date."],
  complete: ["This classroom is already set up", "Return to My Classes to open the active classroom."],
  error: ["We couldn’t load the children", "Try again without leaving the page."],
};

function ClassroomHandoverChildren() {
  const { classId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { identity, identityStatus } = useHandoverIdentity();
  const selection = useMemo(() => parseClassroomHandoverSelection(location.search), [location.search]);
  const children = useClassroomHandoverChildren({
    schoolId: identity?.schoolid,
    classId,
    stagedDraft: location.state?.childReviewDraft,
  });
  const returnToClasses = () => navigate(destinationWithSelection({ url: "/classadmindashboard", ...selection }));
  const backToSetup = () => navigate(
    destinationWithSelection({ url: `/classroom-handover/${classId}/setup`, ...selection }),
    { state: { childReviewDraft: children.state } }
  );

  const finish = async () => {
    try {
      const result = await children.finish();
      if (result?.setupStatus === "complete" || result?.status === "active") returnToClasses();
    } catch (_) {
      // The hook retains the staged change-set and renders an actionable recovery state.
    }
  };

  const renderContent = () => {
    if (identityStatus === "loading" || children.status === "idle" || children.status === "loading") {
      return <div className="spinner-container" role="status" aria-label="Loading children"><div className="big-ball-spinner" /></div>;
    }
    if (identityStatus === "error") {
      return <HandoverRouteState as="main" role="alert" title="We couldn’t verify your account"><button type="button" onClick={returnToClasses}>Return to My Classes</button></HandoverRouteState>;
    }
    const hasLoadedReview = Boolean(children.state && children.entry);
    if (ROUTE_STATE_COPY[children.status] && (children.status !== "error" || !hasLoadedReview)) {
      const [title, message] = ROUTE_STATE_COPY[children.status];
      return <HandoverRouteState as="main" title={title} message={message} role={children.status === "error" ? "alert" : "status"}>
        {children.status === "error" && <button type="button" onClick={() => children.load().catch(() => {})}>Try again</button>}
        <button type="button" onClick={returnToClasses}>Return to My Classes</button>
      </HandoverRouteState>;
    }
    if (!children.state || !children.entry) {
      return <HandoverRouteState as="main" role="alert" title="We couldn’t load the children" message="The classroom response was empty."><button type="button" onClick={() => children.load().catch(() => {})}>Try again</button><button type="button" onClick={returnToClasses}>Return to My Classes</button></HandoverRouteState>;
    }
    return <ChildReview state={children.state} status={children.status} error={children.error} onDispatch={children.dispatch} onFinish={finish} onBack={backToSetup} />;
  };

  return (
    <ClassroomHandoverPageShell identity={identity}>
      {renderContent()}
    </ClassroomHandoverPageShell>
  );
}

export default ClassroomHandoverChildren;
